import type {
  V1CredentialInfo,
  V1ExternalCall,
} from '@/generated/hooks/grimoire/models';

export interface IResolvedStepConnection {
  /** The alias segment from `kognitos.<alias>.<book>.<function>` — also the
   *  key used in the automation's own connections map. */
  connectionAlias: string;
  /** Resolved from the automation's extracted code connections. Undefined
   *  when the alias has no matching connection (e.g. stale/renamed). */
  book?: string;
  bookVersion?: string;
  calls: { functionName: string; call: V1ExternalCall }[];
}

/** Parses a step's `audit.external_calls` (`kognitos.<connection_alias>.
 *  <book_name>.<function>`) and groups them by connection, resolving each
 *  connection's book/version by exact alias match against the automation's
 *  extracted code connections. Calls that don't match this shape are
 *  skipped — not every reachable call is a `kognitos.*` external call. */
export function resolveStepConnections(
  externalCalls: V1ExternalCall[] | undefined,
  credentialsByAlias: ReadonlyMap<string, V1CredentialInfo>,
): IResolvedStepConnection[] {
  const groups = new Map<string, IResolvedStepConnection>();

  for (const call of externalCalls ?? []) {
    const segments = call.call?.split('.') ?? [];
    if (segments.length < 4 || segments[0] !== 'kognitos') continue;

    const connectionAlias = segments[1];
    const functionName = segments.slice(3).join('.');

    let group = groups.get(connectionAlias);
    if (!group) {
      const credential = credentialsByAlias.get(connectionAlias);
      group = {
        connectionAlias,
        book: credential?.book,
        bookVersion: credential?.book_version,
        calls: [],
      };
      groups.set(connectionAlias, group);
    }
    group.calls.push({ functionName, call });
  }

  return Array.from(groups.values());
}
